///
/// EarthBrowser Admin Dashboard - Audit Log Section
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  useGetAuditLogQuery,
  type AuditLogFilters
} from "@/redux/api/permissionsApi"
import {
  Activity,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Filter,
  RefreshCcw,
  User,
} from "lucide-react"
import React, { useState } from "react"

export function AuditLogSection() {
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    limit: 20,
  })
  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  const { data, isLoading, isFetching, refetch } = useGetAuditLogQuery(filters)

  const handleFilterChange = (key: keyof AuditLogFilters, value: string | number) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }))
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  const toggleRow = (id: number) => {
    setExpandedRow(expandedRow === id ? null : id)
  }

  const getActionBadge = (action: string) => {
    const actionLower = action.toLowerCase()

    if (actionLower.includes("create") || actionLower.includes("grant")) {
      return <Badge className="rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">{action}</Badge>
    }
    if (actionLower.includes("delete") || actionLower.includes("revoke") || actionLower.includes("suspend")) {
      return <Badge className="rounded-full bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/20">{action}</Badge>
    }
    if (actionLower.includes("update") || actionLower.includes("edit")) {
      return <Badge className="rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20">{action}</Badge>
    }
    return <Badge variant="secondary" className="rounded-full">{action}</Badge>
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
  }

  const formatJsonValue = (value: unknown): string => {
    if (value === null || value === undefined) return "—"
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2)
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value)
    }
    return JSON.stringify(value)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  const logs = data?.logs || []
  const pagination = data?.pagination

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {pagination && (
            <Badge variant="secondary" className="rounded-full">
              {pagination.total} total events
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {showFilters ? (
              <ChevronDown className="h-4 w-4 ml-2" />
            ) : (
              <ChevronRight className="h-4 w-4 ml-2" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
          >
            <RefreshCcw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {showFilters && (
        <Card className="rounded-xl border border-border bg-background/50 backdrop-blur-sm transition-all duration-300">
          <CardContent className="pt-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <label htmlFor="audit-action" className="text-sm font-medium">Action Type</label>
                <Input
                  id="audit-action"
                  placeholder="e.g., user.created, role.granted"
                  value={filters.action || ""}
                  onChange={(e) => handleFilterChange("action", e.target.value)}
                  className="rounded-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="audit-start-date" className="text-sm font-medium">Start Date</label>
                <Input
                  id="audit-start-date"
                  type="date"
                  value={filters.start_date || ""}
                  onChange={(e) => handleFilterChange("start_date", e.target.value)}
                  className="rounded-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="audit-end-date" className="text-sm font-medium">End Date</label>
                <Input
                  id="audit-end-date"
                  type="date"
                  value={filters.end_date || ""}
                  onChange={(e) => handleFilterChange("end_date", e.target.value)}
                  className="rounded-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ page: 1, limit: 20 })}
                className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {logs.length === 0 ? (
        <Card className="rounded-xl border border-dashed border-purple-500/30 bg-purple-500/5 transition-all duration-300">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="p-3 rounded-full bg-purple-500/10 mb-4">
              <AlertCircle className="h-6 w-6 text-purple-500" />
            </div>
            <p className="text-lg font-medium">No audit logs found</p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your filters or check back later
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden transition-all duration-300">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Affected User</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>IP Address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleRow(log.id)}
                  >
                    <TableCell>
                      <button
                        aria-label={expandedRow === log.id ? "Collapse event details" : "Expand event details"}
                        aria-expanded={expandedRow === log.id}
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleRow(log.id)
                        }}
                        className="p-1 rounded-full hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        {expandedRow === log.id ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {formatDate(log.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>{getActionBadge(log.action)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm truncate max-w-[200px]">
                          {log.actor_email || "System"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm truncate max-w-[200px]">
                        {log.affected_user_email || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-full font-mono text-xs">
                        {log.resource_type}#{log.resource_id}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {log.ip_address || "—"}
                    </TableCell>
                  </TableRow>
                  {expandedRow === log.id && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/30">
                        <div className="p-4 space-y-4">
                          {log.metadata && Object.keys(log.metadata).length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                                <Activity className="h-4 w-4" />
                                Metadata
                              </h4>
                              <pre className="text-xs bg-background p-3 rounded-lg border overflow-auto max-h-48">
                                {formatJsonValue(log.metadata)}
                              </pre>
                            </div>
                          )}

                          {log.old_values && Object.keys(log.old_values).length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold mb-2 text-red-700 dark:text-red-400">
                                Old Values
                              </h4>
                              <pre className="text-xs bg-background p-3 rounded-lg border overflow-auto max-h-48">
                                {formatJsonValue(log.old_values)}
                              </pre>
                            </div>
                          )}

                          {log.new_values && Object.keys(log.new_values).length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold mb-2 text-emerald-700 dark:text-emerald-400">
                                New Values
                              </h4>
                              <pre className="text-xs bg-background p-3 rounded-lg border overflow-auto max-h-48">
                                {formatJsonValue(log.new_values)}
                              </pre>
                            </div>
                          )}

                          {(!log.metadata || Object.keys(log.metadata).length === 0) &&
                            (!log.old_values || Object.keys(log.old_values).length === 0) &&
                            (!log.new_values || Object.keys(log.new_values).length === 0) && (
                              <p className="text-sm text-muted-foreground italic">
                                No additional details available for this event
                              </p>
                            )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total events)
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
            >
              Next
            </Button>
            <Select
              value={String(filters.limit)}
              onValueChange={(value) => handleFilterChange("limit", parseInt(value))}
            >
              <SelectTrigger className="w-[100px] rounded-xl focus:ring-2 focus:ring-blue-500">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="10" className="rounded-lg">10 / page</SelectItem>
                <SelectItem value="20" className="rounded-lg">20 / page</SelectItem>
                <SelectItem value="50" className="rounded-lg">50 / page</SelectItem>
                <SelectItem value="100" className="rounded-lg">100 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  )
}

